import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../../services";
import { useOrder } from "../../context/OrderContext";
import VideoLoader from "../../components/VideoLoader";
import { useAuth } from "../../context/AuthContext";
import { useDelivery } from "../../context/DeliveryContext";
import { loadAsapDelay } from "../../lib/utils";

// Icons as simple SVG components
const LocationIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
      clipRule="evenodd"
    />
  </svg>
);

const ClockIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

const BagIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
    />
  </svg>
);

type OrderDeliveryStatus =
  | "pending"
  | "pickup"
  | "pickup_complete"
  | "dropoff"
  | "received"
  | "delivered"
  | "canceled";

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface OrderItem {
  itemId: string;
  name: string;
  quantity: number;
  price: number;
  discountAmount: number;
  tax: number;
  gst?: number;
  pst?: number;
  prepTime?: number;
  customOptions?: { label: string; price: number; qty: number }[];
}

interface Order {
  _id: string;
  orderNumber: string;
  items: OrderItem[];
  totalAmount: number;
  status: string;
  deliveryStatus?: OrderDeliveryStatus;
  discountAmount: number;
  /** Pre-tax item amount paid from the bonus wallet */
  bonusAmountUsed?: number;
  tipAmount: number;
  createdAt: string;
  phoneNumber?: string;
  tableToken?: number;
  orderBy: string;
  employeeName?: string;
  payChoice?: string;
  deliveryAmount: number;
  deliveryLiveMapUrl: string;
  maplink?: string;
  deliveryTime: number;
  orderStatus: string;
  isScheduled?: boolean;
  scheduledAt?: string;
  scheduledTimezone?: string;
  localScheduledAt?: string;
  orderPrepTime?: number;
  metadata?: {
    pickup_name?: string;
    [key: string]: unknown;
  };
}

const DeliveryTracker: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { recentOrder, fetchOrderStatus } = useOrder();
  const { user, currencySymbol } = useAuth();
  const { orderType, delivery, pickup: pickupData } = useDelivery();
  const currentRestaurant =
    orderType === "pickup"
      ? pickupData.locationObject
      : delivery.restaurantLocation;
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] =
    useState<OrderDeliveryStatus | null>(null);

  const [deliveryStage, setDeliveryStage] = useState(0);

  // Refs to prevent multiple fetches and handle polling
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const lastStatusRef = useRef<OrderDeliveryStatus | null>(null);
  const isMountedRef = useRef(true);

  const stages = [
    { id: 0, label: "Order Received", icon: CheckCircleIcon },
    { id: 1, label: "Preparing", icon: BagIcon },
    { id: 2, label: "On the way", icon: LocationIcon },
    { id: 3, label: "Delivered", icon: CheckCircleIcon },
  ];

  // Map delivery status to stage number
  const statusToStageMap: Record<OrderDeliveryStatus, number> = {
    pending: 0,
    received: 0,
    pickup: 1,
    pickup_complete: 1,
    dropoff: 2,
    delivered: 3,
    canceled: 0,
  };

  // Fetch order details from API
  const fetchOrder = useCallback(async () => {
    if (!orderId || !isMountedRef.current) return;

    try {
      const response = await api.get<ApiResponse<Order>>(`/orders/${orderId}`);
      if (response.data.success && response.data.data && isMountedRef.current) {
        const orderData = response.data.data;
        setOrder(orderData);

        // Update delivery status if available
        if (orderData.deliveryStatus) {
          const newStatus =
            orderData.deliveryStatus.toLowerCase() as OrderDeliveryStatus;
          setCurrentStatus(newStatus);

          setDeliveryStage(statusToStageMap[newStatus] || 0);
        }
      } else {
        setError("Failed to fetch order details");
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      if (isMountedRef.current) {
        setError("Failed to fetch order details");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [orderId]);

  // Initial fetch
  useEffect(() => {
    fetchOrder();
    if (user) fetchOrderStatus();
  }, [user]);

  // Live tracking using recentOrder from context
  useEffect(() => {
    if (!recentOrder) return;

    const newStatus =
      recentOrder[0].deliveryStatus?.toLowerCase() as OrderDeliveryStatus;
    const isFirstLoad = lastStatusRef.current === null;
    const statusChanged = newStatus !== lastStatusRef.current;

    if (newStatus && (isFirstLoad || statusChanged)) {
      console.log("Status update:", newStatus);
      lastStatusRef.current = newStatus;
      setCurrentStatus(newStatus);
      setDeliveryStage(statusToStageMap[newStatus] || 0);

      // Update order data if IDs match
      if (recentOrder[0].orderNumber?.toString() === orderId) {
        setOrder(
          (prev) =>
            ({
              ...prev,
              ...recentOrder[0],
              deliveryStatus: newStatus,
              deliveryLiveMapUrl:
                recentOrder[0].maplink || prev?.deliveryLiveMapUrl || "",
            }) as Order,
        );
      }

      // Stop polling if order is delivered or canceled
      if (newStatus === "delivered" || newStatus === "canceled") {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    }
  }, [recentOrder, orderId]);

  // Setup polling for order status updates

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <VideoLoader size="medium" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-center p-4">
        <p className="text-gray-600 mb-4">{error || "Order not found"}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
        >
          Go Back
        </button>
      </div>
    );
  }

  const mapUrl = order.maplink || order.deliveryLiveMapUrl;

  const restaurantName =
    order.metadata?.pickup_name ||
    currentRestaurant?.hotelName ||
    currentRestaurant?.name;

  // Item tax/gst/pst are stored per unit. Bonus wallet covers items in order
  // (pre-tax) and the covered part carries no tax — same as checkout.
  // PST takes the rounding remainder so GST + PST always equals Taxes.
  const bonusUsed = Number(order.bonusAmountUsed) || 0;
  const itemSubtotal = order.items.reduce((s, item) => s + item.price * item.quantity, 0);
  let remainingBonus = bonusUsed;
  let rawTax = 0;
  let rawGst = 0;
  for (const item of order.items) {
    const subtotal = item.price * item.quantity;
    const covered = Math.min(remainingBonus, subtotal);
    remainingBonus -= covered;
    const taxableShare = subtotal > 0 ? (subtotal - covered) / subtotal : 1;
    rawTax += (item.tax || 0) * item.quantity * taxableShare;
    rawGst += (item.gst || 0) * item.quantity * taxableShare;
  }
  const taxTotal = Number(rawTax.toFixed(2));
  const hasTaxSplit = order.items.some((item) => item.gst != null || item.pst != null);
  const gstTotal = Number(rawGst.toFixed(2));
  const pstTotal = Number((taxTotal - gstTotal).toFixed(2));
  // totalAmount includes the bonus-paid portion; show what the customer paid
  const amountPaid = Math.max(0, order.totalAmount - bonusUsed);

  const formattedScheduledTime = order.scheduledAt
    ? new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: order.scheduledTimezone || undefined,
        timeZoneName: "short",
      }).format(new Date(order.scheduledAt))
    : null;

  // ASAP orders: estimated prep = restaurant's delay at order time + order prep
  // time. The delay is saved per order at checkout so it survives a reload
  // (the delivery context is empty after one).
  const asapDelay =
    loadAsapDelay(order._id) ?? currentRestaurant?.asapDelay ?? 0;
  const prepTime =
    order.orderPrepTime ??
    Math.max(0, ...order.items.map((item) => item.prepTime || 0));
  const estimatedPrepMinutes = asapDelay + prepTime;
  const showPrepEstimate =
    !order.isScheduled &&
    estimatedPrepMinutes > 0 &&
    (!currentStatus ||
      currentStatus === "pending" ||
      currentStatus === "received");

  return (
    <div className="min-h-screen bg-gray-50 dm-confirmation">
      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-4 dm-confirm-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dm-confirm-text">Track Order</h1>
                 {(currentStatus === "pending" || currentStatus === "received") &&
                currentRestaurant?.phoneNo && (
                  <p className="text-xl font-bold text-gray-600 dm-confirm-text-muted mt-1">
                    Contact restaurant for order status:{" "}
                    <a
                      href={`tel:${currentRestaurant.phoneNo}`}
                      className="dm-phone-link text-base font-medium underline"
                    >
                      {currentRestaurant.phoneNo}
                    </a>
                  </p>
                )}
              {restaurantName && (
                <p className="text-lg font-semibold text-gray-800 dm-confirm-text">{restaurantName}</p>
              )}
              <p className="text-gray-600 dm-confirm-text-muted">Order #{order.orderNumber}</p>
              {showPrepEstimate && (
                <p className="my-2 text-sm text-gray-600 dm-confirm-text-muted flex items-center gap-1">
                  <ClockIcon />
                  <span>
                    Your order will be prepared in about{" "}
                    <span className="font-semibold">
                      {estimatedPrepMinutes} min
                    </span>
                  </span>
                </p>
              )}
              {order.isScheduled && (
                <div className="mt-1">
                  {formattedScheduledTime && (
                    <p className="text-sm text-gray-600 dm-confirm-text-muted flex items-center gap-1">
                      <ClockIcon />
                      <span>Scheduled for {formattedScheduledTime}</span>
                    </p>
                  )}
                </div>
              )}
              {currentStatus && (
                <p className="text-sm text-main mt-1 dm-confirm-text">
                  Status:{" "}
                  {currentStatus === "pending" || currentStatus === "received"
                    ? order.orderStatus
                    : stages[deliveryStage].label}
                </p>
              )}
           
            </div>
            {mapUrl && (
              <button
                onClick={() => window.open(mapUrl, "_blank")}
                className="dm-confirm-map-btn dm-white-btn px-4 py-2 bg-black text-white rounded-lg flex items-center space-x-2 transition-colors dm-confirm-icon"
              >
                <LocationIcon />
                <span>View Map</span>
              </button>
            )}
          </div>

          {/* <div className="relative">
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200"></div>
            <div
              className="absolute top-5 left-0 h-0.5 bg-green-500 transition-all duration-200"
              style={{
                width: `${(deliveryStage / (stages.length - 1)) * 100}%`,
              }}
            ></div>

            <div className="relative flex justify-between">
              {stages.map((stage, index) => {
                const Icon = stage.icon;
                const isActive = index <= deliveryStage;
                const isCurrent = index === deliveryStage;

                return (
                  <div key={stage.id} className="flex flex-col items-center">
                    <div
                      className={`
                      w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                      ${
                        isActive
                          ? "bg-green-500 text-white"
                          : "bg-gray-200 text-gray-600"
                      }
                      ${isCurrent ? "ring-4 ring-green-100 scale-110" : ""}
                    `}
                    >
                      <Icon />
                    </div>
                    <p
                      className={`text-xs mt-2 text-center ${
                        isActive ? "text-gray-900 font-medium" : "text-gray-600"
                      }`}
                    >
                      {stage.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div> */}
        </div>

        {/* Order Details */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-4 dm-confirm-card">
          <h2 className="text-lg font-semibold mb-4 flex items-center dm-confirm-text">
            <BagIcon />
            <span className="ml-2">Order Details</span>
          </h2>

          <div className="space-y-3">
            {order.items.map((item, index) => (
              <div
                key={index}
                className="flex justify-between items-start pb-3 border-b last:border-0"
              >
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 dm-confirm-text">
                      {item.quantity}x
                    </span>
                    <span className="ml-2 text-gray-900 dm-confirm-text">{item.name}</span>
                  </div>
                  {item.customOptions && item.customOptions.length > 0 && (
                    <p className="text-sm text-gray-600 dm-confirm-text-muted">
                      {(() => {
                        const removedItems = item.customOptions
                          .filter((opt) => opt.qty === -1)
                          .map((opt) => opt.label);

                        const normalItems = item.customOptions
                          .filter((opt) => opt.qty !== -1)
                          .map((opt) => {
                            if (opt.qty > 1) return `${opt.label} ×${opt.qty}`;
                            return opt.label;
                          });

                        return (
                          <>
                            {normalItems.length > 0 && (
                              <p className="mb-0">{normalItems.join(", ")}</p>
                            )}
                            {removedItems.length > 0 && (
                              <p>Removed - {removedItems.join(", ")}</p>
                            )}
                          </>
                        );
                      })()}
                    </p>
                  )}
                </div>
                <span className="font-medium text-gray-900 dm-confirm-text">
                  {currencySymbol}{(item.price * item.quantity).toFixed(2)}
                </span>
              </div>
            ))}
          </div>

          {/* Price Breakdown */}
          <div className="mt-2 pt-2 border-t space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dm-confirm-text-muted">Item Total</span>
              <span className="dm-confirm-text">{currencySymbol}{itemSubtotal.toFixed(2)}</span>
            </div>
            {bonusUsed > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-green-600">Bonus Balance Discount</span>
                <span className="text-green-600">-{currencySymbol}{bonusUsed.toFixed(2)}</span>
              </div>
            )}
            {taxTotal > 0 && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dm-confirm-text-muted">Taxes</span>
                  <span className="dm-confirm-text">+{currencySymbol}{taxTotal.toFixed(2)}</span>
                </div>
                {hasTaxSplit && (
                  <>
                    <div className="flex justify-between text-[13px] pl-3">
                      <span className="text-gray-500 dm-confirm-text-muted">GST</span>
                      <span className="text-gray-500 dm-confirm-text-muted">{currencySymbol}{gstTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[13px] pl-3">
                      <span className="text-gray-500 dm-confirm-text-muted">PST</span>
                      <span className="text-gray-500 dm-confirm-text-muted">{currencySymbol}{pstTotal.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </>
            )}
            {order.deliveryAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dm-confirm-text-muted">Delivery Fee</span>
                <span className="dm-confirm-text">{currencySymbol}{order.deliveryAmount.toFixed(2)}</span>
              </div>
            )}
              {order.tipAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-black dm-confirm-text">Tip</span>
                  <span className="text-black dm-confirm-text">
                    {currencySymbol}{Number(order.tipAmount).toFixed(2)}
                  </span>
                </div>
              )}
            {/* <div className="flex justify-between text-sm">
              <span className="text-gray-600 dm-confirm-text-muted">Discount</span>
              <span className="dm-confirm-text">-{currencySymbol}{(order.discountAmount ?? 0).toFixed(2)}</span>
            </div> */}
            <div className="flex justify-between text-lg font-semibold pt-2 border-t">
              <span className="dm-confirm-text">Total</span>
              <span className="text-black dm-confirm-text">
                {currencySymbol}{amountPaid.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-4">
          {/* <button className="flex-1 py-3 bg-white border-2 border-[rgba(219,0,7,1)] text-[rgba(219,0,7,1)] rounded-lg font-medium transition-colors hover:bg-red-50">
            Need Help?
          </button> */}
          <button
            onClick={() => {
              if (order?.orderBy === "customer") {
                navigate("/");
              } else {
                navigate("/sales");
              }
            }}
            className="dm-confirm-cta flex-1 py-3 bg-main text-white rounded-lg font-medium transition-colors "
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryTracker;
